import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Activity, AlertCircle, History, Download,
  ChevronLeft, ChevronRight, X
} from 'lucide-react';
import apiClient from '../services/api';

/**
 * SessionHistory — admin report of all login/logout activity.
 *
 * Backend: GET /session-log.php (filters: userid, type, from, to, page)
 * Route suggestion: 'session-history'
 */

const TYPE_STYLES = {
  active:   { label: 'Active',   cls: 'bg-[#DCFCE7] text-[#15803D]' },
  manual:   { label: 'Logout',   cls: 'bg-[#EFF6FF] text-[#1D4ED8]' },
  forced:   { label: 'Forced',   cls: 'bg-[#FEF2F2] text-[#B91C1C]' },
  replaced: { label: 'Replaced', cls: 'bg-[#FFFBEB] text-[#B45309]' },
  expired:  { label: 'Expired',  cls: 'bg-gray-100 text-gray-500' },
};

const SessionHistory = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadLog = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      setError('');
      const params = { page: pageNum, pagesize: 50 };
      if (filterUser) params.userid = filterUser;
      if (filterType) params.type = filterType;
      if (fromDate)   params.from = fromDate;
      if (toDate)     params.to = toDate;

      const res = await apiClient.get('/session-log.php', { params });
      if (res.data.success) {
        const d = res.data.data;
        setRows(d.rows || []);
        setSummary(d.summary || {});
        setTotal(d.total || 0);
        setPage(d.page || 1);
        setPages(d.pages || 1);
      } else {
        setError(res.data.message || 'Failed to load session history');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load session history');
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterType, fromDate, toDate]);

  useEffect(() => { loadLog(1); }, [loadLog]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/Users.php');
        if (res.data.success) setUsers(res.data.data || []);
      } catch { /* dropdown just stays empty */ }
    })();
  }, []);

  const fmtDateTime = (s) => s ? new Date(s.replace(' ', 'T')).toLocaleString() : '—';

  const fmtDuration = (secs) => {
    if (secs == null) return '—';
    const s = parseInt(secs);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ${s % 60}s`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  };

  const typeBadge = (row) => {
    const key = row.logout_time ? (row.logout_type || 'expired') : 'active';
    const t = TYPE_STYLES[key] || TYPE_STYLES.expired;
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-[0.82rem] font-medium ${t.cls}`}>
        {t.label}
      </span>
    );
  };

  const exportCsv = () => {
    const header = ['User', 'Full Name', 'Login Time', 'Logout Time', 'Type', 'Duration', 'IP Address'];
    const lines = rows.map(r => [
      r.username, r.fullname || '',
      r.login_time, r.logout_time || 'active',
      r.logout_time ? (r.logout_type || 'expired') : 'active',
      fmtDuration(r.duration_seconds),
      r.ip_address || ''
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `session-history-page${page}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const clearFilters = () => { setFilterUser(''); setFilterType(''); setFromDate(''); setToDate(''); };
  const hasFilters = filterUser || filterType || fromDate || toDate;

  const inputCls = "px-3 py-2 border-2 border-[#E2E8F0] rounded-lg text-sm text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#2563EB] focus:border-[#2563EB]";

  return (
    <div className="p-6 lg:p-8 min-h-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <History size={24} className="text-[#2563EB]" />
          <h2 className="text-[1.4rem] text-[#1E293B] font-semibold m-0">Session History</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={loading || rows.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[#16A34A] text-white border-0 rounded-lg font-medium cursor-pointer
              transition-colors duration-200 hover:bg-[#15803D] disabled:opacity-50 disabled:cursor-not-allowed">
            <Download size={16} /> Export CSV
          </button>
          <button onClick={() => loadLog(page)} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-[#E2E8F0] rounded-lg
              text-gray-700 font-medium cursor-pointer transition-all duration-200
              hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {['active', 'manual', 'forced', 'replaced', 'expired'].map(key => (
          <div key={key} className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 shadow-sm">
            <div className="text-[1.4rem] font-bold text-[#1E293B]">{summary[key] || 0}</div>
            <div className="mt-1">
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-[0.75rem] font-medium ${TYPE_STYLES[key].cls}`}>
                {TYPE_STYLES[key].label}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 mb-5 shadow-sm">
        <select value={filterUser} onChange={e => setFilterUser(e.target.value)} className={inputCls}>
          <option value="">All Users</option>
          {users.map(u => (
            <option key={u.userid} value={u.userid}>{u.username} — {u.fullname}</option>
          ))}
        </select>

        <select value={filterType} onChange={e => setFilterType(e.target.value)} className={inputCls}>
          <option value="">All Types</option>
          <option value="active">Active (logged in)</option>
          <option value="manual">Manual logout</option>
          <option value="forced">Force logout</option>
          <option value="replaced">Replaced</option>
          <option value="expired">Expired</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-500">
          From
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={inputCls} />
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-500">
          To
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={inputCls} />
        </label>

        {hasFilters && (
          <button onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg cursor-pointer hover:bg-red-100 transition-colors">
            <X size={14} /> Clear
          </button>
        )}

        <span className="ml-auto text-sm text-gray-500 font-medium">{total} records</span>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 mb-5 bg-red-50 border border-red-300 border-l-4 border-l-red-600 rounded-lg">
          <AlertCircle size={18} className="text-red-700 flex-shrink-0" />
          <span className="text-[14px] font-medium text-red-800">{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <Activity size={32} className="text-[#2563EB] animate-spin" />
            <p className="text-gray-500 text-sm">Loading session history...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-[#F1F5F9] border-b-2 border-[#E2E8F0]">
                <tr>
                  {['User', 'Login Time', 'Logout Time', 'Duration', 'Status', 'IP Address'].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-gray-700 text-[0.85rem] uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? rows.map(r => (
                  <tr key={r.logid} className="border-b border-[#E2E8F0] transition-colors duration-150 hover:bg-[#EFF6FF]">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#2563EB]">{r.username}</div>
                      <div className="text-[0.8rem] text-gray-400">{r.fullname}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDateTime(r.login_time)}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDateTime(r.logout_time)}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDuration(r.duration_seconds)}</td>
                    <td className="px-4 py-3">{typeBadge(r)}</td>
                    <td className="px-4 py-3 text-gray-500 text-[0.85rem]">{r.ip_address || '—'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center py-10 text-gray-500">No session records found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-5">
          <button onClick={() => loadLog(page - 1)} disabled={page <= 1 || loading}
            className="flex items-center gap-1 px-3 py-2 bg-white border border-[#E2E8F0] rounded-lg text-sm text-gray-700 cursor-pointer
              hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft size={16} /> Prev
          </button>
          <span className="text-sm text-gray-500 font-medium">Page {page} of {pages}</span>
          <button onClick={() => loadLog(page + 1)} disabled={page >= pages || loading}
            className="flex items-center gap-1 px-3 py-2 bg-white border border-[#E2E8F0] rounded-lg text-sm text-gray-700 cursor-pointer
              hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionHistory;